import { useState } from 'react';
import { HelloMuiButton } from 'components/Mui';
import useUserStore from 'store/useUserStore';
import { PropertyDTO } from 'util/Types';
import { PropertyAdditionalDetail } from 'types/Property';
import ExplorerListingButtonGroup from './ExploreListingButtonGroup';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'util/Constants';

interface PropertyActionButtonProps {
    property: PropertyDTO | null;
    additionalDetails?: PropertyAdditionalDetail | null;
    onClose?: () => void;
}

// Function to determine which plan is needed based on property details
const getRequiredPlan = (property: PropertyDTO): string => {
    const { details } = property;
    const { price, listingType } = details;

    // If price is 0 or null, it's "On Request" - requires Pro plan
    if (!price || price <= 0) {
        return 'Pro';
    }

    if (listingType === 'rental') {
        // For rentals: Entry plan covers up to €3,000/month, others cover all
        if (price <= 3000) {
            return 'Entry';
        } else {
            return 'Plus';
        }
    } else {
        // For sales properties
        if (price <= 400000) {
            return 'Entry';
        } else if (price <= 1000000) {
            return 'Plus';
        } else if (price <= 2000000) {
            return 'Prime';
        } else {
            return 'Pro';
        }
    }
};

const PropertyActionButton = ({ property, additionalDetails, onClose }: PropertyActionButtonProps) => {
    const { userInfo } = useUserStore();
    const navigate = useNavigate();

    const isLoggedIn = !!userInfo;

    if (!property) return null;

    if (isLoggedIn) {
        // Show the explore listing button for logged-in users
        return <ExplorerListingButtonGroup property={property} additionalDetails={additionalDetails} />;
    }

    // For non-logged-in users, show unlock details button with plan info
    const requiredPlan = getRequiredPlan(property);

    const handleUnlockDetails = () => {
        // Close the modal first if onClose function is provided
        if (onClose) {
            onClose();
        }
        // Navigate to sign-up page
        navigate(ROUTES.SIGN_UP);
    };

    return (
        <div className="flex flex-col gap-3">
            <HelloMuiButton
                variant="contained"
                onClick={handleUnlockDetails}
                sx={{
                    borderRadius: '5px',
                    height: '40px',
                    backgroundColor: '#3176bc',
                    color: '#ffffff',
                    fontSize: '14px',
                    '&:hover': {
                        backgroundColor: '#ffffff',
                        color: '#3176bc',
                    },
                }}
                className="truncate"
            >
                Unlock Details
            </HelloMuiButton>

            <div className="text-center">
                <p className="text-xs text-gray-600">
                    Requires <span className="font-semibold text-my-blue">{requiredPlan}</span> plan
                </p>
            </div>
        </div>
    );
};

export default PropertyActionButton;
